import React, {useState, useRef, useEffect} from 'react';
import {ChevronDown, Globe, Check} from 'lucide-react';


const languages = [
    {code: 'en', name: 'English'},
    {code: 'es', name: 'Español'},
    {code: 'zh', name: '中文'},
    {code: 'hi', name: 'हिन्दी'},
    {code: 'ar', name: 'العربية'},
    {code: 'fr', name: 'Français'},
    {code: 'de', name: 'Deutsch'},
    {code: 'ru', name: 'Русский'},
    {code: 'ja', name: '日本語'},
    {code: 'pt', name: 'Português'},
    {code: 'bn', name: 'বাংলা'},
    {code: 'pa', name: 'ਪੰਜਾਬੀ'},
    {code: 'ko', name: '한국어'},
    {code: 'it', name: 'Italiano'},
    {code: 'vi', name: 'Tiếng Việt'},
    {code: 'tr', name: 'Türkçe'},
    {code: 'pl', name: 'Polski'},
    {code: 'uk', name: 'Українська'},
    {code: 'fa', name: 'فارسی'},
    {code: 'nl', name: 'Nederlands'}
];


export default function LanguageSelect({
                                           defaultLanguage = 'en',
                                           onChange,
                                           styles = {}
                                       }) {
    const [selected, setSelected] = useState(
        languages.find(lang => lang.code === defaultLanguage) || languages[0]
    );
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (language) => {
        setSelected(language);
        setIsOpen(false);
        onChange(language);
    };

    // Default styles
    const defaultStyles = {
        container: {
            position: 'relative',
            width: '100%',
            fontFamily: 'system-ui, -apple-system, sans-serif'
        },
        button: {
            width: '100%',
            padding: '0.75rem 0.9rem',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontSize: '15px',
            color: 'var(--text)'
        },
        buttonHover: {
            borderColor: 'var(--blue)',
            boxShadow: 'var(--focus-ring)'
        },
        buttonContent: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
        },
        flag: {
            fontSize: '1.5rem',
            lineHeight: 1
        },
        languageName: {
            fontWeight: '500'
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '14px',
            padding: '6px',
            boxShadow: 'var(--shadow-md)',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease'
        },
        option: {
            padding: '0.6rem 0.75rem',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            transition: 'background 0.15s ease',
            color: '#374151'
        },
        optionHover: {
            background: 'var(--bg)'
        },
        optionSelected: {
            background: 'var(--blue-soft)',
            color: 'var(--blue)'
        },
        optionContent: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
        },
        iconContainer: {
            display: 'flex',
            alignItems: 'center'
        }
    };

    const mergedStyles = {
        container: {...defaultStyles.container, ...styles.container},
        button: {...defaultStyles.button, ...styles.button},
        buttonContent: {...defaultStyles.buttonContent, ...styles.buttonContent},
        flag: {...defaultStyles.flag, ...styles.flag},
        languageName: {...defaultStyles.languageName, ...styles.languageName},
        dropdown: {...defaultStyles.dropdown, ...styles.dropdown},
        option: {...defaultStyles.option, ...styles.option},
        optionContent: {...defaultStyles.optionContent, ...styles.optionContent},
        iconContainer: {...defaultStyles.iconContainer, ...styles.iconContainer}
    };

    return (
        <>
            <style>
                {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
            </style>
            <div ref={dropdownRef} style={mergedStyles.container}>
                <button
                    type="button"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    style={mergedStyles.button}
                    onMouseEnter={(e) => {
                        Object.assign(e.currentTarget.style, defaultStyles.buttonHover);
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--border)';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    <div style={mergedStyles.buttonContent}>
                        <Globe size={18} color="var(--blue)"/>
                        <span style={mergedStyles.flag}>{selected.flag}</span>
                        <span style={mergedStyles.languageName}>{selected.name}</span>
                    </div>
                    <ChevronDown
                        size={20}
                        color="var(--text-muted)"
                        style={{
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                        }}
                    />
                </button>

                {isOpen && (
                    <div style={mergedStyles.dropdown}>
                        {languages.map((language) => {
                            const isSelected = language.code === selected.code;
                            return (
                                <div
                                    key={language.code}
                                    onClick={() => handleSelect(language)}
                                    style={{
                                        ...mergedStyles.option,
                                        ...(isSelected ? defaultStyles.optionSelected : {})
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            Object.assign(e.currentTarget.style, defaultStyles.optionHover);
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.background = 'transparent';
                                        }
                                    }}
                                >
                                    <div style={mergedStyles.optionContent}>
                                        <span style={mergedStyles.flag}>{language.flag}</span>
                                        <span>{language.name}</span>
                                    </div>
                                    {isSelected && (
                                        <div style={mergedStyles.iconContainer}>
                                            <Check size={18} color="var(--blue)"/>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
